import { supabase } from './supabase';

export const maintenanceService = {
  // Maintenance Schedules
  async getMaintenanceSchedules(gymId, filters = {}) {
    try {
      let query = supabase
        ?.from('maintenance_schedules')
        ?.select('*, equipment(*), assigned_technician:user_profiles!assigned_technician_id(full_name, email)')
        ?.eq('gym_id', gymId)
        ?.eq('is_active', true);

      if (filters?.equipmentId) {
        query = query?.eq('equipment_id', filters?.equipmentId);
      }

      if (filters?.technicianId) {
        query = query?.eq('assigned_technician_id', filters?.technicianId);
      }

      const { data, error } = await query?.order('next_scheduled_date', { ascending: true });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async createMaintenanceSchedule(scheduleData) {
    try {
      const { data, error } = await supabase
        ?.from('maintenance_schedules')
        ?.insert(scheduleData)
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async updateMaintenanceSchedule(scheduleId, updates) {
    try {
      const { data, error } = await supabase
        ?.from('maintenance_schedules')
        ?.update(updates)
        ?.eq('id', scheduleId)
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Technician Assignments
  async getTechnicianAssignments(technicianId, filters = {}) {
    try {
      let query = supabase
        ?.from('technician_assignments')
        ?.select('*, work_order:equipment_work_orders(*, equipment(*)), technician:user_profiles!technician_id(full_name, email)')
        ?.eq('technician_id', technicianId);

      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }

      const { data, error } = await query?.order('assigned_at', { ascending: false });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async updateTechnicianAssignment(assignmentId, updates) {
    try {
      const { data, error } = await supabase
        ?.from('technician_assignments')
        ?.update(updates)
        ?.eq('id', assignmentId)
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async acceptWorkOrder(assignmentId) {
    return this.updateTechnicianAssignment(assignmentId, {
      status: 'accepted',
      accepted_at: new Date()?.toISOString()
    });
  },

  async startWorkOrder(assignmentId, location = {}) {
    return this.updateTechnicianAssignment(assignmentId, {
      status: 'in_progress',
      started_at: new Date()?.toISOString(),
      location_latitude: location?.latitude,
      location_longitude: location?.longitude
    });
  },

  async completeWorkOrder(assignmentId) {
    return this.updateTechnicianAssignment(assignmentId, {
      status: 'completed',
      completed_at: new Date()?.toISOString()
    });
  },

  // Facility Impact Tracking
  async getFacilityImpactLogs(gymId, filters = {}) {
    try {
      let query = supabase
        ?.from('facility_impact_logs')
        ?.select('*, work_order:equipment_work_orders(*), equipment(*), created_by:user_profiles!created_by(full_name)')
        ?.eq('gym_id', gymId);

      if (filters?.severity) {
        query = query?.eq('severity', filters?.severity);
      }

      if (filters?.impactType) {
        query = query?.eq('impact_type', filters?.impactType);
      }

      const { data, error } = await query?.order('created_at', { ascending: false });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async createFacilityImpactLog(impactData) {
    try {
      const { data, error } = await supabase
        ?.from('facility_impact_logs')
        ?.insert(impactData)
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async updateFacilityImpactLog(logId, updates) {
    try {
      const { data, error } = await supabase
        ?.from('facility_impact_logs')
        ?.update(updates)
        ?.eq('id', logId)
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Maintenance Alerts
  async getMaintenanceAlerts(gymId, activeOnly = true) {
    try {
      let query = supabase
        ?.from('maintenance_alerts')
        ?.select('*, equipment(*), maintenance_schedule:maintenance_schedules(*)')
        ?.eq('gym_id', gymId);

      if (activeOnly) {
        query = query?.eq('is_active', true);
      }

      const { data, error } = await query?.order('alert_date', { ascending: false });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async acknowledgeAlert(alertId, userId) {
    try {
      const { data, error } = await supabase
        ?.from('maintenance_alerts')
        ?.update({
          acknowledged_by: userId,
          acknowledged_at: new Date()?.toISOString()
        })
        ?.eq('id', alertId)
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async resolveAlert(alertId) {
    try {
      const { data, error } = await supabase
        ?.from('maintenance_alerts')
        ?.update({
          resolved_at: new Date()?.toISOString(),
          is_active: false
        })
        ?.eq('id', alertId)
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Work Order Photos
  async uploadWorkOrderPhoto(photoData) {
    try {
      const { data, error } = await supabase
        ?.from('work_order_photos')
        ?.insert(photoData)
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async getWorkOrderPhotos(workOrderId) {
    try {
      const { data, error } = await supabase
        ?.from('work_order_photos')
        ?.select('*, technician:user_profiles!technician_id(full_name)')
        ?.eq('work_order_id', workOrderId)
        ?.order('captured_at', { ascending: false });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Parts Inventory
  async getPartsInventory(gymId, filters = {}) {
    try {
      let query = supabase
        ?.from('maintenance_parts_inventory')
        ?.select('*')
        ?.eq('gym_id', gymId)
        ?.eq('is_active', true);

      if (filters?.category) {
        query = query?.eq('category', filters?.category);
      }

      if (filters?.lowStock) {
        query = query?.filter('quantity_in_stock', 'lte', 'minimum_stock_level');
      }

      const { data, error } = await query?.order('part_name');

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async updatePartsInventory(partId, updates) {
    try {
      const { data, error } = await supabase
        ?.from('maintenance_parts_inventory')
        ?.update(updates)
        ?.eq('id', partId)
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async recordPartUsage(usageData) {
    try {
      const { data, error } = await supabase
        ?.from('work_order_parts_used')
        ?.insert(usageData)
        ?.select()
        ?.single();

      if (error) throw error;

      // Update inventory quantity
      if (usageData?.part_id && usageData?.quantity_used) {
        await supabase?.rpc('decrement_part_quantity', {
          part_id: usageData?.part_id,
          quantity: usageData?.quantity_used
        });
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async getWorkOrderPartsUsed(workOrderId) {
    try {
      const { data, error } = await supabase
        ?.from('work_order_parts_used')
        ?.select('*, part:maintenance_parts_inventory(*), used_by:user_profiles!used_by(full_name)')
        ?.eq('work_order_id', workOrderId)
        ?.order('used_at', { ascending: false });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Generate scheduled work orders
  async generateScheduledWorkOrders() {
    try {
      const { data, error } = await supabase?.rpc('generate_scheduled_work_orders');

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }
};